import axios, {
  type AxiosRequestConfig,
  type AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

import type { AuthRefreshTokenResponse } from './generated/model/authRefreshTokenResponse';

type RequestMetadata = {
  requestId: string;
  startedAt: number;
  activeRequestCount: number;
};

type TimedAxiosRequestConfig = InternalAxiosRequestConfig & {
  metadata?: RequestMetadata;
};

/**
 * API Client Configuration
 *
 * Platform-specific setup:
 * - Web: Uses relative URLs (same origin), credentials via HTTP-only cookies
 * - Mobile: Uses configured backend URL, credentials via Bearer token
 */

// Create axios instance with default config
export const apiClient = axios.create({
  // Base URL will be set by platform-specific initialization
  // Web: empty (relative URLs)
  // Mobile: user-configured backend URL
  baseURL: '',
  headers: {
    'Content-Type': 'application/json',
  },
  // Web needs this for cookie-based auth
  withCredentials: true,
});

// Track if we're currently refreshing to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: AxiosResponse) => void;
  reject: (error: unknown) => void;
  config: AxiosRequestConfig;
}> = [];
let getTokenRef: (() => string | null) | null = null;
let tokenInterceptorId: number | null = null;
let onRefreshTokens:
  | ((tokens: { accessToken: string; refreshToken?: string }) => Promise<void> | void)
  | null = null;
let nextRequestId = 0;
let activeRequestCount = 0;

function processQueue(error: unknown) {
  failedQueue.forEach(({ reject }) => reject(error));
  failedQueue = [];
}

async function retryQueue() {
  const queue = [...failedQueue];
  failedQueue = [];
  for (const { resolve, reject, config } of queue) {
    try {
      const response = await apiClient.request(config);
      resolve(response);
    } catch (err) {
      reject(err);
    }
  }
}

function getNextRequestId() {
  return `${Date.now()}-${nextRequestId++}`;
}

apiClient.interceptors.request.use((config) => {
  const timedConfig = config as TimedAxiosRequestConfig;
  const now = Date.now();
  activeRequestCount += 1;
  timedConfig.metadata = {
    requestId: timedConfig.metadata?.requestId ?? getNextRequestId(),
    startedAt: now,
    activeRequestCount,
  };
  return timedConfig;
});

apiClient.interceptors.response.use(
  (response) => {
    activeRequestCount = Math.max(0, activeRequestCount - 1);
    return response;
  },
  (error) => {
    activeRequestCount = Math.max(0, activeRequestCount - 1);
    return Promise.reject(error);
  }
);

// Response interceptor for handling 401 errors and token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    // Only handle 401 errors and avoid retrying the refresh endpoint itself
    if (
      error.response?.status !== 401 ||
      !originalRequest ||
      originalRequest.url === '/api/auth/refresh' ||
      originalRequest.url === '/api/auth/login'
    ) {
      return Promise.reject(error);
    }

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject, config: originalRequest });
      });
    }

    isRefreshing = true;

    try {
      // Attempt to refresh the token
      // For web: cookies are sent automatically
      // For mobile: the refresh token should be in the request body (handled by mobile-specific code)
      const refreshResponse = await apiClient.post<AuthRefreshTokenResponse>(
        '/api/auth/refresh',
        {}
      );

      if (onRefreshTokens && refreshResponse.data?.accessToken) {
        await onRefreshTokens({
          accessToken: refreshResponse.data.accessToken,
          refreshToken: refreshResponse.data.refreshToken,
        });
      }

      // Refresh succeeded, retry original request and queued requests
      isRefreshing = false;
      retryQueue();
      return apiClient.request(originalRequest);
    } catch (refreshError) {
      // Refresh failed, reject all queued requests
      isRefreshing = false;
      processQueue(refreshError);
      return Promise.reject(refreshError);
    }
  }
);

/**
 * Configure the API client for a specific platform
 */
export function configureApiClient(options: {
  baseURL?: string;
  getToken?: () => string | null;
  onRefreshTokens?: (tokens: {
    accessToken: string;
    refreshToken?: string;
  }) => Promise<void> | void;
}) {
  if (options.baseURL) {
    apiClient.defaults.baseURL = options.baseURL;
  }

  // Mobile: Add auth token interceptor
  if (options.getToken) {
    getTokenRef = options.getToken;
  }

  if (options.onRefreshTokens) {
    onRefreshTokens = options.onRefreshTokens;
  }

  if (getTokenRef && tokenInterceptorId === null) {
    tokenInterceptorId = apiClient.interceptors.request.use((config) => {
      const token = getTokenRef?.();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }
}

/**
 * Custom instance for Orval-generated hooks
 * This function is called by all generated API functions
 */
export async function customInstance<T>(config: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.request<T>(config);
  return response.data;
}

/**
 * API error response structure
 */
export interface ApiErrorResponse {
  type?: string;
  error?: string;
  message?: string;
  details?: string;
  code?: string;
}

/**
 * Error type for API errors
 */
export type ApiError = AxiosError<ApiErrorResponse>;

/**
 * Extract the best error message from an API error
 * Prioritizes: details > message > error > generic fallback
 */
export function getApiErrorMessage(error: unknown, fallback = 'An error occurred'): string {
  if (!error) return fallback;

  // Check if it's an AxiosError with response data
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ApiErrorResponse | undefined;
    if (data) {
      // Prefer details (most specific) > message > error
      if (data.details) return data.details;
      if (data.message) return data.message;
      if (data.error) return data.error;
    }
    // Fall back to axios error message
    if (error.message) return error.message;
  }

  // Check if it's a regular Error
  if (error instanceof Error) {
    return error.message;
  }

  // Check if it's a string
  if (typeof error === 'string') {
    return error;
  }

  return fallback;
}

export default customInstance;
